
/**
 * 事件数据上报前转换装箱
 * EventMessage[]: { header: {}, content: []}[]
 */

import { HB_EVENT_NAME } from "src/constant";
import { fixDateValue } from "src/utils";

const MAX_EVENT_REPEATS = 2;
const AVERAGE_TRIGGER_INTERVAL = 500;
// timing提取上报的字段以及缩写映射
const TIMING_KEYS = {
  connectEnd: 'ce',
  connectStart: 'cs',
  decodedBodySize: 'dbs',
  domainLookupEnd: 'dle',
  domainLookupStart: 'dls',
  duration: 'd',
  encodedBodySize: 'ebs',
  fetchStart: 'fs',
  initiatorType: 'it',
  redirectEnd: 're',
  redirectStart: 'rs',
  requestStart: 'rqs',
  responseEnd: 'rse',
  responseStart: 'rss',
  secureConnectionStart: 'scs',
  startTime: 'st',
  transferSize: 'ts',
  activationStart: 'as',
  domComplete: 'dc',
  domContentLoadedEventEnd: 'dclee',
  domContentLoadedEventStart: 'dcles',
  domInteractive: 'dia',
  loadEventEnd: 'lee',
  loadEventStart: 'les',
  redirectCount: 'rc',
  type: 't',
  unloadEventEnd: 'uee',
  unloadEventStart: 'ues',
  nextHopProtocol: 'nhp',
  matchType: 'mt',
  cacheType: 'cct',
};
function formatTiming(timing: PerformanceNavigationTiming | PerformanceResourceTiming): Record<string, number|string> {
  const ret = {};
  Object.keys(TIMING_KEYS).forEach(key => {
    const v = timing[key];
    if(typeof v !== 'undefined') {
      if(typeof v === 'number') {
        const val = Math.round(timing[key]);
        // fix可能存在的18446744073709552000异常值的问题
        ret[TIMING_KEYS[key]] = val > Number.MAX_SAFE_INTEGER ? 0 : val;
      } else {
        ret[TIMING_KEYS[key]] = timing[key];
      }
    }
  });
  return ret;
}
// 合并多次触发的相同错误和请求
// 错误事件以错误信息msg值为唯一，进行相同合并判断
// 请求事件(仅GET)以请求地址rurl值为唯一，进行相同合并判断
// 记录重复次数cnt字段
function combineEvents(hbEvents: HBEventData[]): HBEventData[] {
  const countMap = new Map();
  function countEvents(key: string, hbEvent: HBEventData) {
    let v = countMap.get(key) || {
      n: 0,
      firstTime: hbEvent.evt,
    };
    v.n += 1;
    v.lastEv = hbEvent;
    countMap.set(key, v);
  }
  function combineFilter(key: string, item: HBEventData) {
    const v = countMap.get(key);
    const totalTime = v.lastEv.evt - v.firstTime;
    if (v && v.n > MAX_EVENT_REPEATS && (totalTime / v.n) < AVERAGE_TRIGGER_INTERVAL) {
      if (item.eid === v.lastEv.eid) {
        item.cnt = v.n;
        item.attrs = Object.assign({}, item.attrs, {
          iFirstEvt: v.firstTime,
          iEvDuration: Math.round(item.evt - v.firstTime),
        });
        return true;
      }
      return false;
    }
    return true;
  }
  hbEvents.forEach((item) => {
    if (item.en === HB_EVENT_NAME.ERROR) {
      countEvents(item.msg, item);
    }
    if (item.en === HB_EVENT_NAME.REQUEST && (!item.m || String(item.m).toLocaleUpperCase() === 'GET')) {
      countEvents(item.rurl, item);
    }
  });
  return hbEvents.filter(item => {
    if (item.en === HB_EVENT_NAME.ERROR) {
      return combineFilter(item.msg, item);
    }
    if (item.en === HB_EVENT_NAME.REQUEST && (!item.m || String(item.m).toLocaleUpperCase() === 'GET')) {
      return combineFilter(item.rurl, item);
    }
    return true;
  });
}
function converterEventMessage(hbEvents: HBEventData[]): EventMessage {
  if(hbEvents?.length === 0) {
    return null;
  }
  const combEvents = combineEvents(hbEvents);
  combEvents.forEach(item => {
    // 避免可能存在的带有小数点的问题
    item.evt = fixDateValue(item.evt);
    // timing源数据暂时先不做上报
    // delete item.timing;
    // delete item.rtiming;
    if(item.timing) {
      item.timing = formatTiming(<PerformanceResourceTiming>item.timing);
    }
    if(item.rtiming) {
      item.rtiming = formatTiming(<PerformanceResourceTiming>item.rtiming);
    }
    Object.keys(item).forEach(key => {
      if(key === 'attrs' && (!item[key] || JSON.stringify(item[key]) === '{}')) {
        delete item[key];
      }
      if(typeof item[key] === 'undefined' || /^_/.test(key)) {
        delete item[key];
      }
    });
  });
  if(combEvents.length === 1) {
    const { en, eid, evt, ...others } = combEvents[0];
    return {
      header: { ...others },
      content: [ { en, eid, evt } ],
    };
  }
  const map = new Map<string, number>();
  const eventMessage: EventMessage = {
    header: {},
    content: []
  };
  combEvents.forEach(item => {
    Object.entries(item).forEach(([field, value]) => {
      const valueType = typeof value;
      if(valueType === 'string' || valueType === 'number') {
        const key = `${field}${value}`;
        let n = map.get(key) || 0;
        ++n;
        map.set(key, n);
        if(n === combEvents.length) {
          eventMessage.header[field] = value;
        }
      }
    });
  });
  eventMessage.content = combEvents.map((item) => {
    const ret = {} as any;
    Object.keys(item).forEach(key => {
      if(
        typeof eventMessage.header[key] === 'undefined' &&
        !/^_/.test(key) &&
        typeof item[key] !== 'undefined'
      ) {
        ret[key] = item[key];
      }
    });
    return ret;
  });
  // header存在bid但缺失servicecode数据时
  if (eventMessage.header.bid) {
    const first = hbEvents.find(item => item.servicecode);
    // const wbPagetab = hbEvents.find(item => item.en === 'WBPagetab');
    // const wbPagetabAttrs = wbPagetab?.attrs as { type: string, code: string, name: string };
    const click = hbEvents.find(item => item.fieldid);
    // const openOrActive = wbPagetabAttrs && ['open', 'active'].includes(wbPagetabAttrs.type);
    const fieldsToSet = ['servicecode', 'servicename', 'appname', 'appcode', 'labelname', 'labelcode', 'domaincode', 'domaincode'];
    // if (openOrActive) {
    //   eventMessage.header.navtype = `tab_${wbPagetabAttrs.type}`; // 工作台页签打开或激活场景
    //   if (wbPagetabAttrs.name) {
    //     eventMessage.header.msg = wbPagetabAttrs.name; // 工作台页签名称
    //   }
    //   if (wbPagetab.servicecode && wbPagetab.servicecode !== eventMessage.header.servicecode) {
    //     fieldsToSet.forEach(key => eventMessage.header[key] = wbPagetab[key] || '');
    //   }
    // }
    if (!eventMessage.header.servicecode && first) {
      fieldsToSet.forEach(key => eventMessage.header[key] = first[key] || '');
    }
    if (click && click.fieldid) {
      eventMessage.header.fieldid = click.fieldid;
    }
  }
  return eventMessage;
}

export default converterEventMessage;
